import React, { useState, useEffect } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { IExcelFile } from './parseExcelInIframe';
import { ICsvData } from './validation';
import { detectHeaders } from './excelUtils';
import { normalizeColumnName } from './columnUtils';

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    onImport: (sheetName: string | null, columnMapping: Record<number, number>, hasHeaders: boolean) => void;
    excelData?: IExcelFile | null;
    csvData?: ICsvData | null;
    expectedColumns: string[];
}

function hasDataInSheet(rows: (string | number)[][]): boolean {
    if (rows.length === 0) return false;
    return rows.some((row) => row.some((cell) => String(cell).trim() !== ''));
}

export default function ImportModal(props: IProps) {
    const [selectedSheet, setSelectedSheet] = useState<string | null>(null);
    const [hasHeaders, setHasHeaders] = useState<boolean>(true);
    const [columnMapping, setColumnMapping] = useState<Record<number, number>>({});

    const isExcel = !!props.excelData;
    const isCsv = !!props.csvData;

    useEffect(() => {
        if (!props.isOpen) return;

        if (isExcel && props.excelData) {
            const sheetNames = Object.keys(props.excelData.sheets);
            if (sheetNames.length === 0) return;

            const filledSheets = sheetNames.filter((name) => hasDataInSheet(props.excelData!.sheets[name].rows));

            if (filledSheets.length === 0) {
                setSelectedSheet(sheetNames[0]);
                return;
            }

            setSelectedSheet(filledSheets[0]);

            const sheet = props.excelData.sheets[filledSheets[0]];
            if (sheet.rows.length > 0) {
                const firstRow = sheet.rows[0];
                const hasHeadersDetected = detectHeaders(firstRow);
                setHasHeaders(hasHeadersDetected);

                if (hasHeadersDetected) {
                    const mapping: Record<number, number> = {};
                    const normalizedHeaders = firstRow.map(normalizeColumnName);

                    props.expectedColumns.forEach((expectedCol, expectedIdx) => {
                        const normalizedExpectedCol = normalizeColumnName(expectedCol);
                        const colIdx = normalizedHeaders.indexOf(normalizedExpectedCol);
                        if (colIdx !== -1) {
                            mapping[expectedIdx] = colIdx;
                        }
                    });

                    setColumnMapping(mapping);
                } else {
                    const mapping: Record<number, number> = {};
                    props.expectedColumns.forEach((_, idx) => {
                        mapping[idx] = idx;
                    });
                    setColumnMapping(mapping);
                }
            }
        } else if (isCsv && props.csvData) {
            if (props.csvData.headers.length > 0) {
                const hasHeadersDetected = props.csvData.headers.some((header) => header.trim() !== '');
                setHasHeaders(hasHeadersDetected);

                if (hasHeadersDetected) {
                    const mapping: Record<number, number> = {};
                    const normalizedHeaders = props.csvData.headers.map(normalizeColumnName);

                    props.expectedColumns.forEach((expectedCol, expectedIdx) => {
                        const normalizedExpectedCol = normalizeColumnName(expectedCol);
                        const csvColIdx = normalizedHeaders.indexOf(normalizedExpectedCol);
                        if (csvColIdx !== -1) {
                            mapping[expectedIdx] = csvColIdx;
                        }
                    });

                    setColumnMapping(mapping);
                } else {
                    const mapping: Record<number, number> = {};
                    props.expectedColumns.forEach((_, idx) => {
                        mapping[idx] = idx;
                    });
                    setColumnMapping(mapping);
                }
            }
        }
    }, [props.isOpen, props.excelData, props.csvData]);

    if (isExcel && (!props.excelData || !selectedSheet)) return null;
    if (isCsv && !props.csvData) return null;
    if (!isExcel && !isCsv) return null;

    const sheetNames = isExcel ? Object.keys(props.excelData!.sheets) : [];
    const filledSheets = isExcel
        ? sheetNames.filter((name) => hasDataInSheet(props.excelData!.sheets[name].rows))
        : [];

    const currentSheet = isExcel && selectedSheet ? props.excelData!.sheets[selectedSheet] : null;
    if (isExcel && !currentSheet) return null;

    const dataRows = isExcel && currentSheet ? currentSheet.rows : props.csvData!.rows;
    const headers = isExcel
        ? hasHeaders && dataRows.length > 0
            ? dataRows[0]
            : dataRows.length > 0
              ? dataRows[0].map((_, idx) => `Column ${idx + 1}`)
              : []
        : hasHeaders && props.csvData!.headers.length > 0
          ? props.csvData!.headers
          : props.csvData!.headers.length > 0
            ? props.csvData!.headers.map((_, idx) => `Column ${idx + 1}`)
            : [];

    const columns = headers.map((cell, idx) => ({ index: idx, name: String(cell) }));

    const handleSheetChange = (sheetName: string) => {
        setSelectedSheet(sheetName);
        const sheet = props.excelData!.sheets[sheetName];

        if (sheet.rows.length > 0) {
            const firstRow = sheet.rows[0];
            const hasHeadersDetected = detectHeaders(firstRow);
            setHasHeaders(hasHeadersDetected);

            if (hasHeadersDetected) {
                const mapping: Record<number, number> = {};
                const normalizedHeaders = firstRow.map(normalizeColumnName);

                props.expectedColumns.forEach((expectedCol, expectedIdx) => {
                    const normalizedExpectedCol = normalizeColumnName(expectedCol);
                    const colIdx = normalizedHeaders.indexOf(normalizedExpectedCol);
                    if (colIdx !== -1) {
                        mapping[expectedIdx] = colIdx;
                    }
                });

                setColumnMapping(mapping);
            } else {
                const mapping: Record<number, number> = {};
                props.expectedColumns.forEach((_, idx) => {
                    mapping[idx] = idx;
                });
                setColumnMapping(mapping);
            }
        }
    };

    const handleColumnMappingChange = (expectedIdx: number, colIdx: number) => {
        setColumnMapping((prev) => ({
            ...prev,
            [expectedIdx]: colIdx,
        }));
    };

    const handleImport = () => {
        props.onImport(selectedSheet, columnMapping, hasHeaders);
        props.onClose();
    };

    const allColumnsMapped = props.expectedColumns.every(
        (_, idx) => columnMapping[idx] !== undefined && columnMapping[idx] !== -1,
    );

    return (
        <Dialog open={props.isOpen} onClose={props.onClose} className='relative z-10'>
            <DialogBackdrop
                transition
                className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in'
            />
            <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
                <div className='flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0'>
                    <DialogPanel
                        transition
                        className='relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-3xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95'
                    >
                        <div className='absolute right-0 top-0 hidden pr-4 pt-4 sm:block'>
                            <button
                                type='button'
                                onClick={props.onClose}
                                className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-800 focus:ring-offset-2'
                            >
                                <span className='sr-only'>Close</span>
                                <XMarkIcon aria-hidden='true' className='h-6 w-6' />
                            </button>
                        </div>
                        <div>
                            <DialogTitle as='h3' className='text-base font-semibold text-gray-900'>
                                Import {isExcel ? 'Excel' : 'CSV'} File
                            </DialogTitle>
                            <p className='text-sm text-gray-500 mt-2'>
                                {isExcel
                                    ? 'Select the sheet and map the columns from your Excel file to the expected columns.'
                                    : 'Map the columns from your CSV file to the expected columns.'}
                            </p>
                        </div>

                        <div className='mt-4 space-y-4'>
                            {isExcel && filledSheets.length > 1 && (
                                <div>
                                    <label className='block text-sm font-bold text-gray-700 mb-2'>Select Sheet</label>
                                    <div className='flex flex-wrap gap-2'>
                                        {filledSheets.map((sheetName) => (
                                            <button
                                                key={sheetName}
                                                onClick={() => handleSheetChange(sheetName)}
                                                className={clsx('px-4 py-2 text-sm font-medium rounded-md border', {
                                                    'bg-cyan-800 text-white border-cyan-800':
                                                        sheetName === selectedSheet,
                                                    'bg-white text-gray-700 border-gray-300 hover:bg-gray-50':
                                                        sheetName !== selectedSheet,
                                                })}
                                            >
                                                {sheetName}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className='flex items-center mb-2'>
                                    <input
                                        type='checkbox'
                                        checked={hasHeaders}
                                        onChange={(e) => setHasHeaders(e.target.checked)}
                                        className='h-4 w-4 rounded border-gray-300 text-cyan-800 focus:ring-cyan-800'
                                    />
                                    <label className='ml-2 text-sm font-medium text-gray-700'>
                                        First row contains headers
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className='block text-sm font-bold text-gray-700 mb-2'>Column Mapping</label>
                                <div className='space-y-2'>
                                    {props.expectedColumns.map((expectedCol, idx) => (
                                        <div key={idx} className='flex items-center space-x-3'>
                                            <div className='w-1/3 text-sm font-medium text-gray-700'>{expectedCol}</div>
                                            <div className='text-gray-500'>→</div>
                                            <select
                                                value={columnMapping[idx] ?? -1}
                                                onChange={(e) =>
                                                    handleColumnMappingChange(idx, parseInt(e.target.value))
                                                }
                                                className='flex-1 rounded-md border-gray-300 shadow-sm focus:border-cyan-800 focus:ring-cyan-800 text-sm'
                                            >
                                                <option value={-1}>-- Select Column --</option>
                                                {columns.map((col) => (
                                                    <option key={col.index} value={col.index}>
                                                        {col.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {dataRows.length > 0 && (
                                <div>
                                    <label className='block text-sm font-bold text-gray-700 mb-2'>
                                        Preview (first 3 rows)
                                    </label>
                                    <div className='border rounded-md overflow-x-auto'>
                                        <table className='min-w-full divide-y divide-gray-200 text-sm'>
                                            <thead className='bg-gray-50'>
                                                <tr>
                                                    {columns.map((col) => (
                                                        <th
                                                            key={col.index}
                                                            className='px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider max-w-xs truncate'
                                                        >
                                                            {col.name}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className='bg-white divide-y divide-gray-200'>
                                                {dataRows
                                                    .slice(hasHeaders ? 1 : 0, hasHeaders ? 4 : 3)
                                                    .map((row, rowIdx) => (
                                                        <tr key={rowIdx}>
                                                            {row.map((cell, cellIdx) => (
                                                                <td
                                                                    key={cellIdx}
                                                                    className='px-3 py-2 whitespace-nowrap text-gray-900'
                                                                >
                                                                    {String(cell)}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className='mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3'>
                            <button
                                onClick={handleImport}
                                disabled={!allColumnsMapped}
                                className='inline-flex w-full justify-center rounded-md bg-cyan-800 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-cyan-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-800 sm:col-start-2 disabled:opacity-50 disabled:cursor-not-allowed'
                                type='button'
                            >
                                Import
                            </button>
                            <button
                                className='mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-100 sm:col-start-1 sm:mt-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-800'
                                onClick={props.onClose}
                                type='button'
                            >
                                Cancel
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
